import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges, SimpleChange } from '@angular/core';
import { Router } from '@angular/router';
import {StringUtils} from '../../utils/string-utils';
import {Entity, EntityKey} from '../../entity/entity';
import {OrderService} from '../../services/order-service';
import {PersonLoginService} from '../../services/person-login-service';
import {ReleaseLine} from './release-line';
import {ModReleaseComponent} from './mod-release.component';
import {TagReleaseComponent} from './tag-release.component';
import {LineReleaseComponent} from './line-release.component';
import {MatTab, MatTabGroup} from '@angular/material/tabs';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {forkJoin} from 'rxjs';
import {EntityService} from '../../services/entity-service';
import {LeadTimeUtils} from '../../utils/lead-time-utils';


@Component({
    selector: 'order-release',
    templateUrl: './order-release.component.html',
    styleUrls: ['./order-release.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatProgressSpinner, MatTabGroup, MatTab, LineReleaseComponent, TagReleaseComponent, ModReleaseComponent]
})
export class OrderReleaseComponent implements OnChanges, OnInit {
  private readonly personLoginService = inject(PersonLoginService);
  private readonly orderService = inject(OrderService);
  private readonly entityService = inject(EntityService);
  private readonly router = inject(Router);
  readonly entityId = input.required<string>({ alias: 'entity-id' });
  readonly entityRevision = input.required<string>({ alias: 'entity-revision' });


  readonly releaseLines = signal<ReleaseLine[]>(undefined!);
  readonly nonMods = signal<ReleaseLine[]>(undefined!);
  readonly handChangeAllowed = signal<boolean>(true);
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly entityKey = signal<EntityKey>(undefined!);

  private initialize(): void {
    this.initializeFromRouteParams();
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  public releaseGenerated(entity: Entity): void {
    this.resetStatus();
    this.router.navigate(["orders", entity.id.entityId, entity.id.entityRevision]);
  }

  private resetStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(null!);
  }

  private backToMain(): void {
    this.router.navigate(['main']);
  }

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.isBusy.set(true);
      this.personLoginService.getCachedLogin();
      this.initialize();
    }
  }


  private initializeFromRouteParams(): void {
    let idString: string = this.entityId();
    let revisionString: string = this.entityRevision();
    if (idString && idString.length > 0 && !isNaN(+revisionString)) {
      this.entityKey.set(new EntityKey());
      this.entityKey().entityId = idString;
      this.entityKey().entityRevision = +revisionString;
      this.entityKey().entityName = 'CONTRACT';
      forkJoin({
        lines: this.orderService.getReleaseLines(this.entityKey().entityId, this.entityKey().entityRevision),
        entity: this.entityService.getEntityByKey(this.entityKey()),
      }).subscribe(result => {
        this.releaseLines.set(result.lines);
        this.handChangeAllowed.set(!LeadTimeUtils.isStockProgram(result.entity?.leadTimeCode));
        this.nonMods.set(JSON.parse(JSON.stringify(this.releaseLines().filter(line => line.configured && !line.mod))));
        this.isBusy.set(false);
      }, error => this.handleError(error));
    } else {
      this.backToMain();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.initializeFromRouteParams();
    }
  }
}
